import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuthStore } from '@features/auth/store/authStore';
import { getHomeRouteForRole } from '@lib/utils/roleNavigation';
import { Button, Spinner, Alert, DropdownMenu } from '@components/ui';
import { CARD, GHOST, DASHED_RULE_STYLE, INVOICE_STATUS_GROUPS } from '@components/ui/designTokens';
import type { DropdownMenuItem } from '@components/ui';
import { cn } from '@lib/utils/cn';
import { formatNumber, formatPriceDisplay } from '@/utils/format';
import { showSuccess, showError, showWarning, showConfirm, showLoading, closeAlert } from '@/utils/sweetAlert';
import {
  ArrowLeftIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  PrinterIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  DocumentMinusIcon,
  DocumentPlusIcon,
  ArrowDownTrayIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useReportError } from '@features/support';
import { useInvoice } from '../hooks/useInvoices';
import { invoiceService } from '../services/invoiceService';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { useTemplate } from '@features/templates/hooks/useTemplate';
import { QRCodeSVG } from 'qrcode.react';
import { ResendEmailModal } from './ResendEmailModal';
import { OTI_CODES } from '@features/products/constants/taxCategories';
import type { EmailAttachments, EmailReceiver } from '../types/invoice.types';
import { trackingHelpers } from '@/services/mixpanel';
import { getQuickActions, getMenuActions } from '../utils/invoiceActions';

// Mapeo de tipos de documento
const documentTypes: Record<string, string> = {
  '01': 'Factura de Operación Interna',
  '02': 'Factura de Importación',
  '03': 'Factura de Exportación',
  '04': 'Nota de Crédito Referente a una o Varias FE',
  '05': 'Nota de Débito Referente a una o Varias FE',
  '06': 'Nota de Crédito Genérica',
  '07': 'Nota de Débito Genérica',
  '08': 'Factura de Zona Franca',
  '09': 'Reembolso',
  '10': 'Factura de operación extranjera',
};

// Mapeo de formas de pago
const paymentMethods: Record<string, string> = {
  '01': 'Crédito',
  '02': 'Efectivo',
  '03': 'Tarjeta de crédito',
  '04': 'Tarjeta de débito',
  '05': 'Tarjeta Fidelización',
  '06': 'Vale',
  '07': 'Tarjeta de regalo',
  '08': 'Transf./Depósito a cta. Bancaria',
  '09': 'Cheque',
  '10': 'Punto de pago',
  '99': 'Otro',
};

// Mapeo de tasas de ITBMS (códigos a porcentajes)
const itbmsTaxRates: Record<string, number> = {
  '00': 0,    // No aplica ITBMS
  '01': 7,    // ITBMS Tasa 7%
  '02': 10,   // ITBMS Tasa 10%
  '03': 15,   // ITBMS Tasa 15%
  '04': 5,    // ITBMS Tasa 5%
};

// Mapeo de códigos de retención
const retentionCodes: Record<string, { description: string; percentage: string }> = {
  '1': { description: 'Pago por servicio profesional al estado', percentage: '100%' },
  '2': { description: 'Pago por venta de bienes/servicios al estado', percentage: '50%' },
  '3': { description: 'Pago a no domiciliado o empresa exterior', percentage: '100%' },
  '4': { description: 'Pago por compra de bienes/servicios', percentage: '50%' },
  '7': { description: 'Pago a comercio afiliado TC/TD', percentage: '50%' },
  '8': { description: 'Otros (disminución de la retención)', percentage: 'Variable' },
};

// Mapeo de tipo de receptor (DGI B401 · iTipoRec). Mismos textos que usa la
// vista previa del asistente (InvoicePreviewModal).
const receiverTypeLabels: Record<string, string> = {
  '01': 'Contribuyente',
  '02': 'Consumidor Final',
  '03': 'Gobierno',
  '04': 'Extranjero',
};

/* ------------------------------------------------------------------ *
 * Presentacion (Claude Design · FacturaDetalle.dc.html)
 *
 * El diseño trata el detalle como una "hoja" de factura: barra de degradado
 * arriba, esquinas perforadas, reglas punteadas entre bandas y un riel a la
 * derecha con el QR, las acciones y la metadata fiscal. Nada de esto decide
 * que datos se muestran: todos los campos que mostraba la version anterior
 * siguen visibles, solo cambian de envoltorio.
 * ------------------------------------------------------------------ */

/**
 * Tono de estado (label + color) por eInvoiceStatus, resuelto contra los 4
 * estados reales del negocio ante la DGI (`INVOICE_STATUS_GROUPS` en
 * designTokens.ts) en vez de mano-mapear los 6 eInvoiceStatus granulares de
 * Aludra con colores propios como hacia esta pantalla antes. La version
 * anterior no coincidia con la descripcion de negocio: Encolada/Procesando
 * salian en naranja y Pendiente de subir en azul (3 colores para un mismo
 * tramite en curso) y Anulada salia en rojo, igual que Error.
 *
 *   Procesada                (0)     DGI 0920      verde
 *   Autorizado uso de la FE  (2,3,4) DGI 0260      amarillo
 *   Error                    (1)     DGI variable  rojo
 *   Anulada                  (5)     DGI 1040      gris
 *
 * El color va en `style` (no en clases de Tailwind) porque son hex que
 * llegan en runtime: una clase armada por interpolacion, tipo `bg-[${hex}]`,
 * no la detecta el compilador de Tailwind en build. Mismo patron que
 * `StatusChip` en InvoiceTableAtoms.tsx.
 */
function resolveStatusTone(eInvoiceStatus: number): { label: string; bar: string; text: string } | undefined {
  const group = INVOICE_STATUS_GROUPS.find((g) => g.codes.includes(eInvoiceStatus));
  return group ? { label: group.label, bar: group.bar, text: group.text } : undefined;
}

/** Botón de ancho completo del riel de acciones (.action-btn del diseño). */
const RAIL_BTN =
  'inline-flex w-full items-center justify-center gap-2.5 rounded-[10px] border border-[#ececec] bg-white px-3.5 py-[11px] text-[14px] font-semibold text-ink-850 transition-colors hover:border-teal-600 hover:bg-teal-500/[.04] hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white dark:hover:border-teal-500';

/**
 * Acción primaria del riel. El diseño de esta pantalla la dibuja con un
 * degradado teal, pero el resto del portal (Login, Dashboard, Facturas
 * emitidas, Clientes) usa el navy de `CTA`: se mantiene el navy para que no
 * haya dos tratamientos distintos de "acción primaria" en la misma app.
 */
const RAIL_BTN_PRIMARY =
  'cta-sheen inline-flex w-full items-center justify-center gap-2.5 rounded-[10px] bg-ink-850 px-3.5 py-[11px] text-[14px] font-semibold text-white transition-colors hover:bg-[#142d40] disabled:cursor-not-allowed disabled:opacity-60';

/** Rótulo mono/mayúsculas que encabeza cada banda y cada tarjeta del riel. */
const BAND_KICKER = 'font-mono text-[12px] font-bold uppercase tracking-[0.14em] text-secondary-400';
const RAIL_KICKER = 'font-mono text-[11px] uppercase tracking-[0.24em] text-secondary-400';

/**
 * Encabezado de la lista de items. `sticky top-0` + fondo opaco propio: la
 * lista puede traer mil items y se scrollea dentro de su propia caja, asi que
 * el encabezado tiene que quedar fijo. El fondo NO puede ser transparente o
 * las filas se ven por debajo al desplazar.
 */
const TH =
  'sticky top-0 z-10 whitespace-nowrap border-b border-[#ececec] bg-[#fbfbfc] px-2.5 py-3 text-left text-[11px] font-bold uppercase tracking-[0.14em] text-secondary-400 dark:border-secondary-700 dark:bg-secondary-900';
const TD = 'px-2.5 py-4 align-top text-[14px] text-ink-850 dark:text-white';
/** Celdas de impuesto (ITBMS / ISC / OTI): tono secundario, no compiten con el total. */
const TD_TAX = 'px-2.5 py-4 align-top text-right font-mono text-[13px] text-secondary-500 dark:text-secondary-300';
/** Celda de total de la fila: es el dato que se busca, va en tinta fuerte. */
const TD_TOTAL =
  'px-2.5 py-4 align-top text-right font-mono text-[14px] font-bold text-ink-850 dark:text-white';
/** Valor en cero o ausente: gris claro, para que el ojo lo saltee. */
const NIL = 'text-secondary-300 dark:text-secondary-600';

/** Regla punteada horizontal del diseño. */
function DashedRule({ className }: { className?: string }) {
  return <div aria-hidden className={cn('h-px', className)} style={DASHED_RULE_STYLE} />;
}

/**
 * Fila etiqueta/valor de las tarjetas de emisor y receptor (.kv del diseño).
 * Van como divs y no como dt/dd porque entre pares se intercala la regla
 * punteada, y un <dl> no admite mezclar dt/dd con otros elementos.
 */
function KvRow({ label, mono, children }: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="text-[12px] font-semibold text-secondary-400">{label}</div>
      <div
        className={cn(
          'min-w-0 break-words text-[14px] font-semibold text-ink-850 dark:text-white',
          mono && 'font-mono font-bold'
        )}
      >
        {children}
      </div>
    </>
  );
}

/**
 * Botón "copiar" de los campos largos del riel (CUFE y protocolo). Solo copia
 * el valor que ya está en pantalla; no lo modifica ni lo envía a ningún lado.
 */
function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      })
      .catch(() => {
        // Sin permiso de portapapeles no hay nada que informar: el valor
        // sigue visible y seleccionable en pantalla.
      });
  };

  return (
    <button
      type="button"
      aria-label={label}
      onClick={handleCopy}
      className="inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-secondary-400 transition-colors hover:text-ink-850 dark:hover:text-white"
    >
      {copied ? <CheckIcon className="h-2.5 w-2.5" /> : <ClipboardDocumentIcon className="h-2.5 w-2.5" />}
      {copied ? 'Copiado' : 'Copiar'}
    </button>
  );
}

/** Bloque de dato del riel: rótulo chico arriba, valor abajo. */
function RailField({ label, action, mono, children }: {
  label: string;
  action?: React.ReactNode;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-secondary-400">{label}</span>
        {action}
      </div>
      <div
        className={cn(
          'mt-1 flex flex-wrap items-baseline gap-1.5 break-words text-[14px] font-bold text-ink-850 dark:text-white',
          mono && 'font-mono'
        )}
      >
        {children}
      </div>
    </div>
  );
}

const MONTH_ABBR = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

/**
 * Fecha con el sello del diseño (`04 · AGO · 2026`). Si el valor no parsea se
 * devuelve tal cual en vez de "Invalid Date".
 */
function formatStampDate(value?: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return `${String(parsed.getDate()).padStart(2, '0')} · ${MONTH_ABBR[parsed.getMonth()]} · ${parsed.getFullYear()}`;
}

/** Iniciales para la baldosa en degradado del emisor. */
function initialsOf(name?: string): string {
  const words = (name || '')
    .replace(/[^\p{L}\p{N} ]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '—';
  return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
}

// Mock data para la factura
/*
const _mockInvoiceDetail = {
  id: '57bb0e6e-9b9b-4487-9c0f-a67fbea10dff',
  invoiceNumber: 'FE-2024-001',
  cufe: 'FE123456789012345678901234567890123456789012',
  authProtocol: 'PROT-2024-001',
  documentType: 'Factura Electrónica',
  status: 'approved', // approved, pending, rejected, draft
  statusMessage: 'Autorizado el uso de la FE',
  issueDate: '2024-10-15',
  dueDate: '2024-11-15',

  // Emisor
  issuer: {
    name: 'Empresa Demo S.A.',
    ruc: '0000000-0-000000',
    dv: '00',
    address: 'Calle Principal, Edificio Corporativo',
    city: 'Ciudad de Panamá',
    country: 'Panamá',
    branchOffice: 'Sucursal Principal',
    phone: '+507 200-0000',
  },

  // Receptor
  receiver: {
    name: 'Tech Solutions S.A.',
    ruc: '1234567-1-123456',
    dv: '12',
    address: 'Calle 50, Edificio Plaza 2000, Piso 10',
    city: 'Ciudad de Panamá',
    country: 'Panamá',
    email: 'contacto@techsolutions.com',
    phone: '+507 300-1234',
  },

  // Items
  items: [
    {
      id: '1',
      code: 'PROD-001',
      name: 'Laptop Dell Inspiron 15',
      description: 'Laptop Dell Inspiron 15, Intel i5, 8GB RAM, 256GB SSD',
      quantity: 2,
      unitPrice: 750.00,
      discount: 0,
      taxRate: 7,
      subtotal: 1500.00,
      taxAmount: 105.00,
      total: 1605.00,
    },
    {
      id: '2',
      code: 'PROD-002',
      name: 'Monitor Samsung 24"',
      description: 'Monitor Samsung 24 pulgadas, Full HD, HDMI',
      quantity: 3,
      unitPrice: 180.00,
      discount: 5,
      taxRate: 7,
      subtotal: 540.00,
      taxAmount: 37.80,
      total: 577.80,
    },
  ],

  // Pagos
  paymentMethods: [
    { method: 'Transferencia Bancaria', amount: 2182.80 },
  ],

  // Totales
  subtotal: 2040.00,
  totalDiscount: 27.00,
  totalTax: 142.80,
  grandTotal: 2182.80,
  currency: 'USD',

  qrCode: '/images/No-image-available.png',
};
*/

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const homeRoute = getHomeRouteForRole(user?.role);

  const { data: invoiceData, isLoading, error } = useInvoice(id || '');
  // Asistente de soporte: lo usa el boton "¿Por que se rechazo?" de la tarjeta de
  // estado. Si el agente no esta configurado en el BFF, reportError no hace nada.
  const reportError = useReportError();

  /**
   * Errores de ACCIONES reales sobre la factura (anular, generar CAFE, reenviar
   * email o a la DGI). Ademas de mostrar el aviso, se los pasa al asistente para
   * que explique la causa: son justo los casos donde el mensaje del backend no le
   * dice nada al cliente. Las validaciones triviales ("no se pudo obtener el ID")
   * siguen con showError a secas: ahi no hay nada que explicar.
   */
  const showErrorWithAssist = (title: string, message: string) => {
    reportError({ title, message, screen: 'detalle de factura' });
  };
  const { selectedCompany, isLoadingSelectedCompany, refetchSelectedCompany } = useCompanies({ navigateOnSelect: false });
  const { data: templateData } = useTemplate();
  const cafeTemplateId = templateData?.template?.cafeTemplate || 'default';

  // Ensure selected company is loaded when page mounts
  useEffect(() => {
    if (!selectedCompany && !isLoadingSelectedCompany) {
      refetchSelectedCompany();
    }
  }, [selectedCompany, isLoadingSelectedCompany, refetchSelectedCompany]);
  const [isGeneratingCAFE, setIsGeneratingCAFE] = useState(false);
  const [cafeUrl, setCAfeUrl] = useState<string | null>(null);
  const [isResendEmailModalOpen, setIsResendEmailModalOpen] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);

  // Track invoice viewed
  useEffect(() => {
    if (invoiceData && invoiceData.id) {
      const isCorrection = invoiceData.documentType === '04' || invoiceData.documentType === '05';
      const totalAmount = (invoiceData as any).grandTotal || (invoiceData as any).total || 0;

      if (isCorrection) {
        // Track credit/debit note viewed
        trackingHelpers.trackCreditNoteView(
          invoiceData.id,
          invoiceData.invoiceNumber,
          totalAmount,
          selectedCompany?.id || 0
        );
      } else {
        // Track regular invoice viewed
        trackingHelpers.trackInvoiceView(
          invoiceData.id,
          invoiceData.invoiceNumber,
          invoiceData.documentType || '01',
          totalAmount,
          selectedCompany?.id || 0,
          false
        );
      }
    }
  }, [invoiceData, selectedCompany]);

  // Handlers para las acciones del menú
  const handlePrintCAFE = async () => {
    if (isLoadingSelectedCompany) {
      showWarning('Cargando...', 'Por favor espera mientras se carga la información de la empresa.');
      return;
    }
    if (!id || !selectedCompany?.id) {
      showError('Error', 'No se pudo obtener la información necesaria');
      return;
    }

    // El CAFE se regenera SIEMPRE, también cuando la factura ya tiene uno.
    //
    // Antes, si `invoiceData.cafe` traía una URL, se abría ese PDF y se salía
    // sin volver a generar. El PDF almacenado es una foto del momento en que se
    // emitió, así que lo que se configura después en Configuración → Templates
    // ("Página Extra" y "Pie de Página") no aparecía NUNCA en una factura que
    // ya tuviera CAFE — por más que se guardara bien y se limpiara la caché del
    // navegador. Aludra sí lee esos campos de `ebillSettings` al generar; el
    // problema era que no se le volvía a pedir.
    //
    // `sendEmail` va en false al reimprimir: el correo al cliente pertenece a
    // la emisión, y mandarlo otra vez en cada impresión sería mucho peor que el
    // bug que esto arregla. Solo la primera generación lo envía.
    const existingUrl = invoiceData?.cafe || cafeUrl;
    const esReimpresion = Boolean(existingUrl);

    setIsGeneratingCAFE(true);
    showLoading(
      esReimpresion ? 'Actualizando CAFE...' : 'Generando CAFE...',
      'Por favor espera mientras se genera el documento.'
    );
    try {
      const result = await invoiceService.generateCAFE(id, selectedCompany.id, {
        templateId: cafeTemplateId,
        sendEmail: !esReimpresion,
      });
      closeAlert();

      if (result.success && result.cafeUrl) {
        setCAfeUrl(result.cafeUrl);
        window.open(result.cafeUrl, '_blank');
      } else if (existingUrl) {
        // Si la regeneración no devuelve documento pero ya había uno, se abre
        // el anterior: es preferible un CAFE desactualizado a ninguno.
        window.open(existingUrl, '_blank');
      } else {
        showError('Error', result.message || 'No se pudo generar el CAFE');
      }
    } catch (error: any) {
      closeAlert();
      if (existingUrl) {
        window.open(existingUrl, '_blank');
        return;
      }
      showErrorWithAssist('Error al generar CAFE', error.message || 'Por favor, intenta de nuevo.');
    } finally {
      setIsGeneratingCAFE(false);
    }
  };

  // Check if invoice can be cancelled (within 7 days of creation)
  const canCancelInvoice = () => {
    if (!invoiceData?.createdDate) return false;

    const createdDate = new Date(invoiceData.createdDate);
    const currentDate = new Date();
    const daysDifference = Math.floor((currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

    return daysDifference <= 7;
  };

  const handleCancelInvoice = async () => {
    if (isLoadingSelectedCompany) {
      showWarning('Cargando...', 'Por favor espera mientras se carga la información de la empresa.');
      return;
    }
    if (!id || !selectedCompany?.id || !selectedCompany?.code) {
      showError('Error', 'No se pudo obtener la información necesaria para anular la factura');
      return;
    }

    // Check if invoice can be cancelled
    if (!canCancelInvoice()) {
      showWarning(
        'No se puede anular',
        'Solo se pueden anular facturas dentro de los primeros 7 días de su creación.'
      );
      return;
    }

    const result = await showConfirm(
      '¿Anular Factura?',
      '¿Estás seguro de que deseas anular esta factura? Esta acción no se puede deshacer.'
    );

    if (result.isConfirmed) {
      showLoading('Anulando Factura...', 'Por favor espera mientras se procesa la anulación.');
      try {
        const cancelResult = await invoiceService.cancelInvoice(
          id,
          selectedCompany.id,
          selectedCompany.code,
          'Anulación de factura'
        );
        closeAlert();

        if (cancelResult.success) {
          await showSuccess(
            '¡Factura Anulada!',
            cancelResult.message || 'La factura se ha anulado exitosamente.'
          );
          // Reload the page to reflect the updated status
          window.location.reload();
        } else {
          showErrorWithAssist('Error al anular', cancelResult.message || 'No se pudo anular la factura');
        }
      } catch (error: any) {
        closeAlert();
        console.error('Error canceling invoice:', error);
        showErrorWithAssist('Error al anular factura', error.message || 'Por favor, intenta de nuevo.');
      }
    }
  };

  const handleCreateCreditNote = () => {
    if (!id) {
      showError('Error', 'No se pudo obtener el ID de la factura');
      return;
    }
    console.log('Crear Nota de Crédito para factura:', id);
    // Navigate to wizard with query params: type=credit, ref=invoiceId
    navigate(`/invoices/new?type=credit&ref=${id}`);
  };

  const handleCreateDebitNote = () => {
    if (!id) {
      showError('Error', 'No se pudo obtener el ID de la factura');
      return;
    }
    console.log('Crear Nota de Débito para factura:', id);
    // Navigate to wizard with query params: type=debit, ref=invoiceId
    navigate(`/invoices/new?type=debit&ref=${id}`);
  };

  const handleCorrectionSameCufe = () => {
    if (!id) {
      showError('Error', 'No se pudo obtener el ID de la factura');
      return;
    }
    // Navigate to wizard with correction mode (same CUFE)
    navigate(`/invoices/new?mode=correction&sameCufe=true&ref=${id}`);
  };

  const handleCorrectionNewCufe = () => {
    if (!id) {
      showError('Error', 'No se pudo obtener el ID de la factura');
      return;
    }
    // Navigate to wizard with correction mode (new CUFE)
    navigate(`/invoices/new?mode=correction&sameCufe=false&ref=${id}`);
  };

  const handleGoBack = () => {
    // Check if there's navigation state indicating where we came from
    const fromPath = location.state?.from;

    if (fromPath) {
      // If we have a "from" path in the state, navigate back to it
      navigate(fromPath);
    } else {
      // Otherwise, try to use browser history or default to main invoices
      navigate(-1);
    }
  };

  const handleResendEmail = async (attachments: EmailAttachments, receivers: EmailReceiver[]) => {
    if (isLoadingSelectedCompany) {
      showWarning('Cargando...', 'Por favor espera mientras se carga la información de la empresa.');
      return;
    }
    if (!id || !selectedCompany?.id) {
      showError('Error', 'No se pudo obtener la información necesaria');
      return;
    }

    setIsResendingEmail(true);

    try {
      const result = await invoiceService.resendInvoiceEmail(
        id,
        selectedCompany.id,
        attachments,
        receivers
      );

      setIsResendingEmail(false);
      setIsResendEmailModalOpen(false);

      if (result.success) {
        await showSuccess('¡Email Enviado!', result.message || 'El email se ha enviado exitosamente');
      } else {
        showErrorWithAssist('Error al enviar', result.message || 'Error al enviar el email');
      }
    } catch (error) {
      setIsResendingEmail(false);
      setIsResendEmailModalOpen(false);
      await showError('Error al enviar', 'Ocurrió un error inesperado al enviar el email');
    }
  };

  const handleResendToDGI = async () => {
    if (!id) {
      showError('Error', 'No se pudo obtener el ID de la factura');
      return;
    }

    const result = await showConfirm(
      '¿Reenviar a DGI?',
      '¿Estás seguro de que deseas reenviar esta factura a la DGI?'
    );

    if (result.isConfirmed) {
      showLoading('Reenviando a DGI...', 'Por favor espera mientras se procesa el reenvío.');
      try {
        const resendResult = await invoiceService.resendToDGI(id);
        closeAlert();

        if (!resendResult.success) {
          showErrorWithAssist('Error al reenviar', resendResult.responseMessage || 'No se pudo reenviar la factura a la DGI');
          return;
        }

        const code = resendResult.responseCode;
        const msg = `Código: ${code} - ${resendResult.responseMessage || ''}`;

        if (resendResult.pending) {
          await showWarning(
            'Reenvío enviado',
            resendResult.responseMessage || 'El reenvío al DGI fue enviado pero su respuesta tardó más de lo esperado. Verifica el estado de la factura en unos minutos.'
          );
        } else if (code === '0920') {
          await showSuccess('Factura Procesada', msg);
          window.location.reload();
        } else if (code === '0260') {
          await showSuccess('Factura Autorizada', msg);
          window.location.reload();
        } else {
          await showWarning(
            'Factura sigue encolada',
            code
              ? `La DGI respondió con código ${code}: ${resendResult.responseMessage || 'Sin mensaje'}. La factura no fue procesada aún.`
              : 'La DGI no devolvió un código de respuesta. La factura sigue encolada.'
          );
        }
      } catch (error: any) {
        closeAlert();
        showErrorWithAssist('Error al reenviar', error.message || 'Por favor, intenta de nuevo.');
      }
    }
  };

  // Qué acciones aplican según el estado de la factura (ver
  // features/invoices/utils/invoiceActions.ts — cubierto por tests
  // unitarios, sin necesitar QA/backend para verificar la matriz).
  const quickActions = getQuickActions(invoiceData?.eInvoiceStatus, {
    hasXml: !!invoiceData?.xml,
    hasCafe: !!invoiceData?.cafe,
    hasQrCode: !!invoiceData?.qrCode,
  });
  const menuActionIds = getMenuActions(invoiceData?.eInvoiceStatus);

  // Menú de acciones — se arma solo con los items habilitados por
  // getMenuActions() para el estado actual.
  const actionMenuItems: DropdownMenuItem[] = [];

  if (menuActionIds.includes('correctionSameCufe')) {
    actionMenuItems.push({
      label: 'Hacer corrección (mismo CUFE)',
      icon: WrenchScrewdriverIcon,
      onClick: handleCorrectionSameCufe,
      variant: 'default',
      'data-testid': 'invoice-detail-correction-same-cufe-button',
    });
  }
  if (menuActionIds.includes('correctionNewCufe')) {
    actionMenuItems.push({
      label: 'Hacer corrección (nuevo CUFE)',
      icon: WrenchScrewdriverIcon,
      onClick: handleCorrectionNewCufe,
      variant: 'default',
      'data-testid': 'invoice-detail-correction-new-cufe-button',
    });
  }
  if (menuActionIds.includes('resendToDgi')) {
    // Nota: "Ver CAFE", "Reenviar Email" e "Imprimir CAFE" se removieron de este
    // menú por estar duplicadas con los botones debajo del QR (misma función).
    // getMenuActions() solo incluye 'resendToDgi' cuando eInvoiceStatus===2
    // (Encolada), así que este item nunca se muestra deshabilitado — se oculta
    // por completo para el resto de estados.
    actionMenuItems.push({
      label: 'Reenviar a DGI',
      icon: ArrowPathIcon,
      onClick: handleResendToDGI,
      variant: 'default',
      'data-testid': 'invoice-detail-resend-dgi-button',
    });
  }
  if (menuActionIds.includes('cancelInvoice')) {
    actionMenuItems.push({
      label: canCancelInvoice() ? 'Anular Factura' : 'Anular Factura (solo 7 días)',
      icon: XMarkIcon,
      onClick: handleCancelInvoice,
      variant: 'danger',
      disabled: !canCancelInvoice(),
      'data-testid': 'invoice-detail-cancel-button',
    });
  }
  if (menuActionIds.includes('creditNote') || menuActionIds.includes('debitNote')) {
    actionMenuItems.push({ divider: true, label: '', onClick: () => { } });
  }
  if (menuActionIds.includes('creditNote')) {
    actionMenuItems.push({
      label: 'Nota de Crédito',
      icon: DocumentMinusIcon,
      onClick: handleCreateCreditNote,
      variant: 'default',
      'data-testid': 'invoice-detail-credit-note-button',
    });
  }
  if (menuActionIds.includes('debitNote')) {
    actionMenuItems.push({
      label: 'Nota de Débito',
      icon: DocumentPlusIcon,
      onClick: handleCreateDebitNote,
      variant: 'default',
      'data-testid': 'invoice-detail-debit-note-button',
    });
  }
  if (menuActionIds.includes('cloneInvoice')) {
    actionMenuItems.push({ divider: true, label: '', onClick: () => { } });
    actionMenuItems.push({
      label: 'Clonar Factura',
      icon: DocumentDuplicateIcon,
      onClick: () => navigate(`/invoices/new?mode=clone&ref=${id}`),
      variant: 'default',
      'data-testid': 'invoice-detail-clone-button',
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !invoiceData) {
    return (
      <Alert variant="danger" title="Error al cargar la factura" data-testid="invoice-detail-error-alert">
        No se pudo cargar la información de la factura. Por favor, intenta de nuevo.
      </Alert>
    );
  }

  // Extract data from the payload
  const payload = invoiceData.payload || {};
  const dGen = payload.dGen || {};
  const emis = dGen.gEmis || {};
  const rec = dGen.gDatRec || {};
  const tot = payload.gTot || {};
  const items: any[] = payload.gItem || [];
  const payments: any[] = tot.gFormaPago || [];

  const docCode: string | undefined = dGen.iDoc;
  const docTypeName = docCode ? documentTypes[docCode] || 'Detalle de Factura' : 'Detalle de Factura';
  const emissionStamp = formatStampDate(dGen.dFechaEm);

  const receiverTypeLabel = rec.iTipoRec ? receiverTypeLabels[String(rec.iTipoRec)] || String(rec.iTipoRec) : null;

  const statusCode = invoiceData.eInvoiceStatus;
  const statusTone = typeof statusCode === 'number' ? resolveStatusTone(statusCode) : undefined;
  // Detalle que ya mostraba la tarjeta de estado para Error y Anulada. Si
  // coincide con la etiqueta del estado no se repite debajo.
  const rawStatusDetail =
    statusCode === 1 || statusCode === 5
      ? [invoiceData.responseCode, invoiceData.resolution].filter(Boolean).join(' - ') ||
        (statusCode === 1 ? 'Error en procesamiento' : 'Anulada')
      : null;
  const statusDetail = rawStatusDetail && rawStatusDetail !== statusTone?.label ? rawStatusDetail : null;

  const hasOtherTaxes =
    parseFloat(tot.dTotISC || 0) > 0 || (tot.gOTITotal && tot.gOTITotal.length > 0);
  const retention = tot.gRetenc;
  const hasRetention = retention && (retention.cCodRetenc || retention.dCodRet);

  return (
    <div className="font-display">
      {/* CAFE Success Alert */}
      {cafeUrl && (
        <Alert variant="success" title="CAFE Generado Exitosamente" data-testid="invoice-detail-cafe-success-alert" className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p>El CAFE se ha generado correctamente y está disponible para descargar.</p>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
              onClick={() => window.open(cafeUrl, '_blank')} data-testid="invoice-detail-download-cafe-button"
            >
              Descargar CAFE
            </Button>
          </div>
        </Alert>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0">
          {/*
            Mismo patron de breadcrumb que el resto del portal: los segmentos
            previos al ultimo van al tamaño chico de breadcrumb, y el ultimo
            ("Detalle de factura") salta al tamaño de titulo.
          */}
          <nav className="flex flex-wrap items-baseline gap-2" aria-label="breadcrumb">
            <Link to={homeRoute} className="text-[14px] text-secondary-400 transition-colors hover:text-ink-850 dark:hover:text-white">
              Principal
            </Link>
            <span className="text-[14px] text-secondary-300">/</span>
            <Link to="/home/main" className="text-[14px] text-secondary-400 transition-colors hover:text-ink-850 dark:hover:text-white">
              Facturas Activas
            </Link>
            <span className="text-[14px] text-secondary-300">/</span>
            <span className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">
              Detalle de factura
            </span>
          </nav>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2.5">
          {/* Menú de acciones */}
          <DropdownMenu
            data-testid="invoice-detail-actions-menu"
            trigger={
              <span className={cn(GHOST, 'cursor-pointer font-semibold')} data-testid="invoice-detail-actions-button">
                <EllipsisVerticalIcon className="h-3.5 w-3.5" />
                Acciones
              </span>
            }
            items={actionMenuItems}
          />
          <button type="button" className={cn(GHOST, 'font-semibold')} onClick={handleGoBack} data-testid="invoice-detail-back-button">
            <ArrowLeftIcon className="h-3.5 w-3.5" />
            Volver
          </button>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* ===================== HOJA DE LA FACTURA ===================== */}
        <div className="relative overflow-hidden rounded-[18px] border border-[#ececec] bg-white shadow-[0_1px_2px_rgba(10,34,51,.03),0_24px_60px_-30px_rgba(10,34,51,.2)] dark:border-secondary-700 dark:bg-secondary-800">
          {/* Barra de degradado y esquinas perforadas del diseño. */}
          <span
            aria-hidden
            className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-teal-500 via-[#3a7ac7] to-[#6b239e]"
          />
          <span
            aria-hidden
            className="absolute -left-2.5 top-7 h-5 w-5 rounded-full border border-[#ececec] bg-[#fafafa] dark:border-secondary-700 dark:bg-secondary-900"
          />
          <span
            aria-hidden
            className="absolute -right-2.5 top-7 h-5 w-5 rounded-full border border-[#ececec] bg-[#fafafa] dark:border-secondary-700 dark:bg-secondary-900"
          />

          {/* --------- Cabecera de la hoja --------- */}
          <div className="grid grid-cols-1 items-center gap-5 px-6 pb-3.5 pt-5 sm:grid-cols-[minmax(0,1fr)_auto] sm:px-9">
            <div className="min-w-0">
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-secondary-400">
                Documento electrónico
              </div>
              <div className="mt-1.5 text-[22px] font-extrabold leading-tight tracking-[-0.03em] text-ink-850 dark:text-white">
                {docTypeName}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-3.5 gap-y-1.5 font-mono text-[13px] text-secondary-500 dark:text-secondary-400">
                <span>
                  N.º{' '}
                  <span className="font-bold text-ink-850 dark:text-white">
                    {dGen.dNroDF || invoiceData.billNumber || 'N/A'}
                  </span>
                </span>
                {emissionStamp && (
                  <>
                    <span aria-hidden className="h-2.5 w-px bg-secondary-200 dark:bg-secondary-700" />
                    <span>
                      Fecha <span className="font-bold text-ink-850 dark:text-white">{emissionStamp}</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <DashedRule className="mx-6 sm:mx-8" />

          {/* --------- Emisor / Receptor ---------
              Dos tarjetas independientes en vez de dos columnas separadas por
              una linea de 1px: como un solo bloque continuo, los datos del
              emisor y los del receptor se leian como una sola ficha. Cada una
              lleva ahora franja de acento (violeta emisor / teal receptor),
              fondo tenue propio y su rotulo sobre pastilla del mismo tono.
              No cambia ningun campo: son los mismos datos, agrupados. */}
          <div className="grid grid-cols-1 gap-4 px-6 py-7 sm:px-9 md:grid-cols-2 md:gap-5">
            {/* Emisor */}
            <section className="relative overflow-hidden rounded-2xl border border-[#6b239e]/20 bg-white py-5 pl-6 pr-5 dark:border-[#6b239e]/35 dark:bg-secondary-800">
              <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[#6b239e]" />
              <div className="inline-flex items-center gap-2 rounded-full bg-[#6b239e]/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.24em] text-[#6b239e] dark:bg-[#6b239e]/25 dark:text-[#cfa8ec]">
                <span aria-hidden className="h-[7px] w-[7px] rounded-full bg-[#6b239e]" />
                De · Emisor
              </div>

              <div className="mt-4 flex items-center gap-3.5">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-[#6b239e] text-[15px] font-extrabold tracking-[-0.02em] text-white">
                  {initialsOf(emis.dNombEm)}
                </span>
                <div className="min-w-0 text-[15px] font-extrabold leading-tight tracking-[-0.015em] text-ink-850 dark:text-white">
                  {emis.dNombEm || 'N/A'}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-[84px_minmax(0,1fr)] items-baseline gap-x-3.5 gap-y-2">
                <KvRow label="RUC" mono>
                  {emis.gRucEmi?.dRuc || 'N/A'}
                  {emis.gRucEmi?.dDV && (
                    <span className="font-normal text-secondary-400"> · DV {emis.gRucEmi.dDV}</span>
                  )}
                </KvRow>
                <DashedRule className="col-span-2" />
                <KvRow label="Sucursal" mono>{emis.dSucEm || 'N/A'}</KvRow>
                <DashedRule className="col-span-2" />
                <KvRow label="Ubicación">{emis.dDirecEm || 'N/A'}</KvRow>
                <DashedRule className="col-span-2" />
                <KvRow label="Teléfono" mono>{emis.dTfnEm || 'N/A'}</KvRow>
              </div>
            </section>

            {/* Receptor */}
            <section className="relative overflow-hidden rounded-2xl border border-teal-500/25 bg-white py-5 pl-6 pr-5 dark:border-teal-500/35 dark:bg-secondary-800">
              <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-teal-500" />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2 rounded-full bg-teal-500/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.24em] text-[#0d9488] dark:bg-teal-500/25 dark:text-teal-200">
                  <span aria-hidden className="h-[7px] w-[7px] rounded-full bg-teal-500" />
                  Para · Receptor
                </div>
                {receiverTypeLabel && (
                  <span className="rounded-full bg-[#f4f4f4] px-2 py-[3px] font-mono text-[11px] font-bold uppercase tracking-[0.10em] text-secondary-500 dark:bg-secondary-900 dark:text-secondary-300">
                    {receiverTypeLabel}
                  </span>
                )}
              </div>

              <div className="mt-4 min-w-0 break-words text-[15px] font-extrabold leading-tight tracking-[-0.015em] text-ink-850 dark:text-white">
                {rec.dNombRec || ''}
              </div>

              <div className="mt-5 grid grid-cols-[84px_minmax(0,1fr)] items-baseline gap-x-3.5 gap-y-2">
                <KvRow label="RUC" mono>
                  {rec.gRucRec?.dRuc || ''}
                  {rec.gRucRec?.dDV && (
                    <span className="font-normal text-secondary-400"> · DV {rec.gRucRec.dDV}</span>
                  )}
                </KvRow>
                <DashedRule className="col-span-2" />
                <KvRow label="Ubicación">{rec.dDirecRec || ''}</KvRow>
                <DashedRule className="col-span-2" />
                <KvRow label="Correo" mono>
                  <span className="break-all text-[13px]">{rec.dCorElectRec || ''}</span>
                </KvRow>
                <DashedRule className="col-span-2" />
                <KvRow label="Teléfono" mono>{rec.dTfnRec || ''}</KvRow>
              </div>
            </section>
          </div>

          <DashedRule className="mx-6 sm:mx-8" />

          {/* --------- Lista de productos --------- */}
          <div className="px-6 py-7 sm:px-9">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
              <div className="flex flex-wrap items-baseline gap-2.5">
                <span className={BAND_KICKER}>§ ÍTEMS</span>
                <span className="text-[15px] font-extrabold tracking-[-0.015em] text-ink-850 dark:text-white">
                  Lista de Productos
                </span>
              </div>
              {items.length > 0 && (
                <span className="inline-flex items-baseline gap-2 font-mono text-[12px] text-secondary-400">
                  <span>
                    {items.length} {items.length === 1 ? 'producto' : 'productos'}
                  </span>
                  {items.length > 12 && (
                    <span className="rounded bg-[#f4f4f4] px-1.5 py-px text-[11px] text-secondary-500 dark:bg-secondary-900 dark:text-secondary-300">
                      desplazá la lista para ver todos
                    </span>
                  )}
                </span>
              )}
            </div>

            {items.length > 0 ? (
              /*
                Tope de alto con scroll propio: una factura puede traer mas de
                mil items y sin tope la hoja se volvia interminable, dejando los
                totales y el resto de la pantalla a un scroll larguisimo de
                distancia. Ahora la lista se desplaza dentro de su caja, el
                encabezado queda fijo (ver TH) y los totales quedan siempre
                cerca. El tope solo aplica cuando hay muchos items: con pocos, la
                caja se ajusta a su contenido.
              */
              <div
                className={cn(
                  '-mx-2.5 overflow-x-auto rounded-xl border border-[#f0f0f0] dark:border-secondary-700',
                  items.length > 12 && 'max-h-[60vh] overflow-y-auto'
                )}
              >
                {/* 9 columnas: en pantallas medianas la tabla scrollea en
                    horizontal dentro de la hoja en vez de recortarse. */}
                <table className="w-full min-w-[700px] border-collapse" data-testid="invoice-detail-products-table">
                  <thead>
                    <tr className="border-b border-[#f0f0f0] dark:border-secondary-700">
                      <th className={TH}>Código</th>
                      <th className={TH}>Descripción</th>
                      <th className={cn(TH, 'text-right')}>Cant.</th>
                      <th className={cn(TH, 'text-right')}>Precio Unit.</th>
                      <th className={cn(TH, 'text-right')}>Desc.</th>
                      <th className={cn(TH, 'text-right')}>ITBMS</th>
                      <th className={cn(TH, 'text-right')}>ISC</th>
                      <th className={cn(TH, 'text-right')}>OTI</th>
                      <th className={cn(TH, 'bg-white text-right text-ink-850 dark:bg-secondary-900 dark:text-white')}>
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f6f6f7] dark:divide-secondary-700">
                    {items.map((item: any, index: number) => {
                      // Obtener el código de tasa ITBMS y convertirlo al porcentaje real
                      const taxCode = item.gITBMSItem?.dTasaITBMS || '00';
                      const taxRate = itbmsTaxRates[taxCode] || 0;
                      const taxAmount = parseFloat(item.gITBMSItem?.dValITBMS || 0);

                      // ISC
                      const iscRate = parseFloat(item.gISCItem?.dTasaISC || 0);
                      const iscAmount = parseFloat(item.gISCItem?.dValISC || 0);

                      // OTI
                      const otiItems = item.gOTIItem || [];

                      const discount = parseFloat(item.gPrecios?.dPrUnitDesc || 0);
                      const rowTotal = parseFloat(item.gPrecios?.dValTotItem || 0);

                      return (
                        /*
                          Jerarquia de color de la fila (antes todo era del mismo
                          gris y las mil filas se leian como una mancha):
                          zebra tenue para seguir el renglon, numero de orden y
                          codigo en tono secundario, impuestos en TD_TAX, total
                          en TD_TOTAL sobre fondo levemente tintado, y los ceros
                          en gris claro (NIL) para que el ojo los saltee.
                        */
                        <tr
                          key={index}
                          data-testid={`invoice-detail-product-row-${index}`}
                          className="even:bg-[#fbfbfc] hover:bg-teal-500/[.04] dark:even:bg-secondary-900/40 dark:hover:bg-secondary-700/40"
                        >
                          <td className={cn(TD, 'font-mono text-[13px] font-semibold text-secondary-500 dark:text-secondary-300')}>
                            {item.dCodProd || 'N/A'}
                          </td>
                          <td className={cn(TD, 'font-medium')}>{item.dDescProd || 'N/A'}</td>
                          <td className={cn(TD, 'text-right font-mono font-semibold')}>
                            {formatNumber(parseFloat(item.dCantCodInt) || 0, 2)}
                          </td>
                          <td className={cn(TD, 'text-right font-mono font-semibold')}>
                            ${formatPriceDisplay(parseFloat(item.gPrecios?.dPrUnit || 0))}
                          </td>
                          <td className={cn(TD_TAX, discount === 0 && NIL)}>
                            {formatPriceDisplay(discount)}
                          </td>
                          <td className={TD_TAX}>
                            <div className={cn('text-[11px] font-normal', taxRate === 0 ? NIL : 'text-secondary-400')}>
                              {taxRate}%
                            </div>
                            <div className={cn(taxAmount === 0 && NIL)}>${formatPriceDisplay(taxAmount)}</div>
                          </td>
                          <td className={TD_TAX}>
                            {iscAmount > 0 ? (
                              <>
                                <div className="text-[11px] font-normal text-secondary-400">{iscRate}%</div>
                                <div>${formatPriceDisplay(iscAmount)}</div>
                              </>
                            ) : (
                              <span className={NIL}>—</span>
                            )}
                          </td>
                          <td className={TD_TAX}>
                            {otiItems.length > 0 ? (
                              <div className="min-w-[100px]">
                                {otiItems.map((oti: any, otiIdx: number) => {
                                  const otiCode = OTI_CODES.find(c => c.code === oti.dCodOTI);
                                  const otiValue = parseFloat(oti.dValOTI || 0);
                                  return (
                                    <div key={otiIdx} className="text-[11px]">
                                      {otiCode?.code}: ${formatNumber(otiValue)}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className={NIL}>—</span>
                            )}
                          </td>
                          <td className={cn(TD_TOTAL, 'bg-white dark:bg-secondary-900/40', rowTotal === 0 && NIL)}>
                            ${formatPriceDisplay(rowTotal)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-secondary-200 py-10 text-center text-[14px] text-secondary-500 dark:border-secondary-700 dark:text-secondary-400">
                No hay productos en esta factura
              </div>
            )}
          </div>

          <DashedRule className="mx-6 sm:mx-8" />

          {/* --------- Formas de pago + Totales --------- */}
          <div className="grid grid-cols-1 gap-8 px-6 pb-8 pt-7 sm:px-9 lg:grid-cols-[minmax(0,1fr)_minmax(300px,auto)] lg:items-start">
            {/* Formas de pago */}
            <div className="min-w-0">
              <div className="mb-3.5 flex flex-wrap items-baseline gap-2.5">
                <span className={BAND_KICKER}>§ PAGOS</span>
                <span className="text-sm font-extrabold text-ink-850 dark:text-white">Formas de Pago</span>
              </div>
              {payments.length > 0 ? (
                <div className="space-y-2">
                  {payments.map((payment: any, index: number) => {
                    const paymentCode = payment.iFormaPago;
                    const paymentDescription = paymentMethods[paymentCode] || payment.dFormaPagoDesc || paymentCode;

                    return (
                      <div
                        key={index}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#eef0f2] bg-[#fbfbfc] px-4 py-3.5 dark:border-secondary-700 dark:bg-secondary-900"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] bg-teal-500/10 text-[#0d9488]">
                            <CreditCardIcon className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="text-[14px] font-bold text-ink-850 dark:text-white">
                              {paymentDescription}
                            </div>
                            {paymentCode && paymentMethods[paymentCode] && (
                              <div className="mt-0.5 font-mono text-[12px] text-secondary-400">
                                Método {paymentCode}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="font-mono text-sm font-bold text-ink-850 dark:text-white">
                          USD ${formatNumber(parseFloat(payment.dVlrCuota || 0))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-secondary-200 py-6 text-center text-[14px] text-secondary-500 dark:border-secondary-700 dark:text-secondary-400">
                  Sin formas de pago registradas
                </div>
              )}
            </div>

            {/* Totales */}
            <div className="min-w-0 lg:min-w-[300px]">
              <div className="mb-3.5">
                <span className={BAND_KICKER}>§ TOTALES</span>
              </div>
              <div className="rounded-xl border border-teal-500/[.16] bg-gradient-to-br from-teal-500/[.04] to-[#6b239e]/[.03] px-5 py-4">
                <div className="flex items-baseline justify-between gap-3.5">
                  <span className="text-[13px] text-secondary-500 dark:text-secondary-400">Subtotal</span>
                  <span className="font-mono text-[14px] font-bold text-ink-850 dark:text-white">
                    ${formatNumber(parseFloat(tot.dTotNeto || 0))}
                  </span>
                </div>
                <DashedRule className="my-2.5" />
                <div className="flex items-baseline justify-between gap-3.5">
                  <span className="text-[13px] text-secondary-500 dark:text-secondary-400">Descuentos</span>
                  <span className="font-mono text-[14px] font-bold text-[#b8503a]">
                    -${formatNumber(parseFloat(tot.dTotDesc || 0))}
                  </span>
                </div>
                <DashedRule className="my-2.5" />
                <div className="flex items-baseline justify-between gap-3.5">
                  <span className="text-[13px] text-secondary-500 dark:text-secondary-400">ITBMS</span>
                  <span className="font-mono text-[14px] font-bold text-ink-850 dark:text-white">
                    ${formatNumber(parseFloat(tot.dTotITBMS || 0))}
                  </span>
                </div>

                {/* Otros Impuestos (ISC + OTI) */}
                {hasOtherTaxes && (
                  <div className="mt-3 rounded-[10px] border border-[#6b239e]/[.18] bg-[#6b239e]/[.04] px-3.5 py-3">
                    <div className="mb-2 text-[12px] font-bold uppercase tracking-[0.14em] text-[#6b239e]">
                      Otros Impuestos
                    </div>

                    {/* ISC */}
                    {parseFloat(tot.dTotISC || 0) > 0 && (
                      <div className="mb-1 flex items-baseline justify-between gap-3">
                        <span className="text-[13px] text-secondary-500 dark:text-secondary-400">ISC</span>
                        <span className="font-mono text-[13px] font-bold text-ink-850 dark:text-white">
                          ${formatNumber(parseFloat(tot.dTotISC || 0))}
                        </span>
                      </div>
                    )}

                    {/* OTI */}
                    {tot.gOTITotal && tot.gOTITotal.length > 0 && (
                      <>
                        {tot.gOTITotal.map((oti: any, index: number) => {
                          const otiCode = OTI_CODES.find(c => c.code === oti.dCodOTITotal);
                          return (
                            <div key={index} className="mb-1 flex items-baseline justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <span className="font-mono text-[13px] font-bold text-secondary-600 dark:text-secondary-300">
                                  {oti.dCodOTITotal}
                                </span>
                                <span className="ml-2 text-[12px] text-secondary-400">
                                  {otiCode?.description || 'Otro'}
                                </span>
                              </div>
                              <span className="font-mono text-[13px] font-bold text-ink-850 dark:text-white">
                                ${formatNumber(parseFloat(oti.dValOTITotal || 0))}
                              </span>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}

                {/* Retención */}
                {hasRetention && (
                  <div className="mt-3 rounded-[10px] border border-accent-500/25 bg-accent-500/[.06] px-3.5 py-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <span className="text-[13px] font-bold text-ink-850 dark:text-white">
                          Retención ({retentionCodes[retention.cCodRetenc || retention.dCodRet]?.percentage || ''})
                        </span>
                        <p className="mt-1 text-[12px] leading-[1.5] text-secondary-500 dark:text-secondary-400">
                          {retentionCodes[retention.cCodRetenc || retention.dCodRet]?.description || 'Retención aplicada'}
                        </p>
                      </div>
                      <span className="font-mono text-[13px] font-bold text-[#d17a12]">
                        ${formatNumber(parseFloat(retention.cValRetenc || retention.dValRet || 0))}
                      </span>
                    </div>
                  </div>
                )}

                <div aria-hidden className="my-3.5 h-0.5 bg-ink-850/[.08] dark:bg-white/10" />
                <div className="flex items-baseline justify-between gap-3.5">
                  <span className="text-sm font-extrabold tracking-[-0.015em] text-ink-850 dark:text-white">
                    Total
                  </span>
                  <span className="font-mono text-[22px] font-extrabold tracking-[-0.015em]">
                    <span className="mr-1 text-[13px] font-bold text-secondary-400">USD</span>
                    <span className="text-ink-850 dark:text-white">
                      ${formatNumber(parseFloat(tot.dVTot || 0))}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* --------- Pie sellado --------- */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-dashed border-[#e2e8f0] bg-[#fafafa] px-6 py-5 dark:border-secondary-700 dark:bg-secondary-900/40 sm:px-9">
            <div className="flex items-center gap-2.5 text-[12px] text-secondary-500 dark:text-secondary-400">
              <ShieldCheckIcon className="h-3.5 w-3.5" />
              Cumple normativa <span className="font-bold text-ink-850 dark:text-white">DGI · Panamá</span>
            </div>
            {docCode && (
              <div className="font-mono text-[11px] uppercase tracking-[0.06em] text-secondary-400">
                Tipo {docCode} · {docTypeName}
              </div>
            )}
          </div>
        </div>

        {/* ===================== RIEL DERECHO ===================== */}
        <aside className="flex flex-col gap-4 xl:sticky xl:top-[84px]">
          {/* Información general: QR + estado */}
          <div className={cn(CARD, 'px-5 py-3.5')}>
            <div className={cn(RAIL_KICKER, 'mb-3.5')}>Información General</div>

            <div className="flex justify-center">
              {invoiceData.qrCode ? (
                <div className="rounded-xl border border-[#ececec] bg-white p-3 shadow-[0_1px_2px_rgba(10,34,51,.04)] dark:border-secondary-600">
                  <QRCodeSVG value={invoiceData.qrCode} size={192} level="H" includeMargin={true} />
                </div>
              ) : (
                <div className="flex h-[218px] w-[218px] items-center justify-center rounded-xl border border-dashed border-secondary-200 bg-secondary-50 px-3 text-center text-[13px] text-secondary-500 dark:border-secondary-600 dark:bg-secondary-900 dark:text-secondary-400">
                  QR Code no disponible
                </div>
              )}
            </div>


            {/* Estado */}
            {statusTone && (
              <div
                className="mt-4 rounded-[10px] border px-3.5 py-3 text-center"
                style={{ borderColor: `${statusTone.bar}40`, background: `${statusTone.bar}14` }}
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: statusTone.bar }} />
                  <span className="text-[14px] font-bold tracking-[0.02em]" style={{ color: statusTone.text }}>
                    {statusTone.label}
                  </span>
                </div>
                {statusDetail && (
                  /*
                    `overflow-wrap:anywhere`: los mensajes de la DGI traen el CUFE
                    completo (~90 caracteres sin espacios) y sin regla de quiebre
                    la cadena se salia de la tarjeta. Las palabras normales siguen
                    cortando por espacio; solo el token largo se parte.
                  */
                  <p
                    className="mt-1.5 break-words [overflow-wrap:anywhere] text-[13px] leading-[1.5]"
                    style={{ color: statusTone.text }}
                  >
                    {statusDetail}
                  </p>
                )}

                {/* Consulta al asistente, solo en facturas rechazadas (estado 1).
                    Es el momento en que el cliente esta frustrado y mirando un
                    codigo que no entiende, asi que le damos la explicacion ahi
                    mismo en vez de que tenga que ir a buscarla.

                    Le pasamos el codigo y la resolucion de la DGI ya separados
                    (no el texto concatenado) y ademas el XML: con el XML el agente
                    corre el validador contra el XSD oficial y puede decir QUE
                    CAMPO esta mal, en lugar de interpretar solo el codigo. */}
                {statusCode === 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      reportError({
                        title: 'Factura rechazada por la DGI',
                        message:
                          'La DGI rechazó esta factura. Explicame por qué se rechazó y cómo lo corrijo.',
                        details: [
                          {
                            code: String(invoiceData.responseCode || ''),
                            message: String(invoiceData.resolution || 'Error en procesamiento'),
                          },
                        ],
                        screen: 'detalle de factura rechazada',
                        /*
                          Preferimos el XML: el agente lo valida contra el XSD
                          oficial. Pero en varias facturas rechazadas viene vacio
                          —el documento nunca llego a armarse del todo—, y ahi el
                          `payload` (el JSON con el detalle completo que el portal
                          envio) es lo unico que describe que se intento emitir.
                          Sin ninguno de los dos, el agente solo tendria el codigo.
                        */
                        invoiceXml: invoiceData.xml || undefined,
                        invoicePayload: !invoiceData.xml && invoiceData.payload ? invoiceData.payload : undefined,
                        /*
                          `response` es la respuesta cruda de la DGI y el UNICO
                          lugar donde viene `gResProcList`. La tarjeta de estado
                          muestra solo el codigo principal, asi que sin esto el
                          agente tampoco veria el resto: hay facturas con 20
                          hallazgos que son un mismo problema repetido item por
                          item mas el que realmente bloquea.
                        */
                        dgiResponse: invoiceData.response || invoiceData.pacResponse || undefined,
                        invoiceRef: {
                          // `invoiceNumber` es un campo legacy de los mocks; el real
                          // que llega de Aludra es `documentNumber`.
                          number: invoiceData.documentNumber || invoiceData.invoiceNumber || undefined,
                          cufe: invoiceData.cufe || undefined,
                          status: statusTone.label,
                        },
                      })
                    }
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-ink-850 px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#142d40]"
                    data-testid="invoice-detail-ask-assistant-button"
                  >
                    <SparklesIcon className="h-4 w-4" />
                    ¿Por qué se rechazó?
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Acciones */}
          <div className={cn(CARD, 'flex flex-col gap-2 px-4 py-3')}>
            {/* getQuickActions() decide que botones mostrar segun eInvoiceStatus
                (Error: solo Ver CFU XML; Encolada: solo Ver XML; resto: todas). */}
            {quickActions.includes('viewCfuXml') && invoiceData.xml && (
              <button type="button" className={RAIL_BTN} onClick={() => window.open(invoiceData.xml, '_blank')} data-testid="invoice-detail-view-cfu-xml-button">
                <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                Ver CFU XML
              </button>
            )}
            {quickActions.includes('resendEmail') && (
              <button
                type="button"
                className={RAIL_BTN_PRIMARY}
                data-testid="invoice-detail-resend-email-button"
                onClick={() => {
                  console.log('🔵 Reenviar Email button clicked');
                  setIsResendEmailModalOpen(true);
                }}
              >
                <EnvelopeIcon className="h-3.5 w-3.5" />
                Reenviar Email
              </button>
            )}
            {quickActions.includes('printCafe') && (
              <button
                type="button"
                className={RAIL_BTN}
                onClick={handlePrintCAFE}
                disabled={isGeneratingCAFE}
                data-testid="invoice-detail-print-cafe-button"
              >
                <PrinterIcon className="h-3.5 w-3.5" />
                {isGeneratingCAFE ? 'Generando...' : 'Imprimir CAFE'}
              </button>
            )}
            {quickActions.includes('viewCafe') && invoiceData.cafe && (
              <button type="button" className={RAIL_BTN} onClick={() => window.open(invoiceData.cafe, '_blank')} data-testid="invoice-detail-view-cafe-button">
                <PrinterIcon className="h-3.5 w-3.5" />
                Ver CAFE (PDF)
              </button>
            )}
            {quickActions.includes('viewXml') && invoiceData.xml && (
              <button type="button" className={RAIL_BTN} onClick={() => window.open(invoiceData.xml, '_blank')} data-testid="invoice-detail-view-xml-button">
                <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                Ver XML
              </button>
            )}
            {quickActions.includes('viewDgi') && invoiceData.qrCode && (
              <button type="button" className={RAIL_BTN} onClick={() => window.open(invoiceData.qrCode, '_blank')} data-testid="invoice-detail-view-dgi-button">
                <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                Ver en DGI
              </button>
            )}
          </div>

          {/* Metadata fiscal */}
          <div className={cn(CARD, 'px-5 py-3.5')}>
            <div className={cn(RAIL_KICKER, 'mb-3.5')}>Metadata fiscal</div>

            <div className="flex flex-col gap-3">
              <RailField label="Tipo de documento">
                {docCode ? (
                  <>
                    <span>{documentTypes[docCode] || docCode}</span>
                    <span className="rounded bg-[#6b239e]/10 px-1.5 py-px font-mono text-[11px] font-bold text-[#6b239e]">
                      {docCode}
                    </span>
                  </>
                ) : (
                  'N/A'
                )}
              </RailField>

              <DashedRule />

              <RailField label="N° de documento" mono>
                <span className="break-all">{dGen.dNroDF || invoiceData.billNumber || 'N/A'}</span>
              </RailField>

              <DashedRule />

              <RailField
                label="CUFE"
                action={invoiceData.cufe ? <CopyButton value={`FE${invoiceData.cufe}`} label="Copiar CUFE" /> : undefined}
              >
                <span className="w-full break-all rounded-lg border border-[#eef0f2] bg-[#fbfbfc] px-2.5 py-2 font-mono text-[12px] font-semibold leading-[1.5] dark:border-secondary-700 dark:bg-secondary-900">
                  {invoiceData.cufe ? `FE${invoiceData.cufe}` : 'N/A'}
                </span>
              </RailField>

              <DashedRule />

              <RailField
                label="Protocolo de autorización"
                action={
                  invoiceData.authProtocol ? (
                    <CopyButton value={invoiceData.authProtocol} label="Copiar protocolo de autorización" />
                  ) : undefined
                }
              >
                <span className="w-full break-all rounded-lg border border-[#eef0f2] bg-[#fbfbfc] px-2.5 py-2 font-mono text-[12px] font-semibold leading-[1.5] dark:border-secondary-700 dark:bg-secondary-900">
                  {invoiceData.authProtocol || 'N/A'}
                </span>
              </RailField>

              <DashedRule />

              <RailField label="Fecha de emisión" mono>
                {emissionStamp || 'N/A'}
              </RailField>
            </div>
          </div>
        </aside>
      </div>

      {/* Modal de Reenvío de Email */}
      <ResendEmailModal
        isOpen={isResendEmailModalOpen}
        onClose={() => setIsResendEmailModalOpen(false)}
        onSubmit={handleResendEmail}
        isSubmitting={isResendingEmail}
      />
    </div>
  );
}
